import type { ApiClient } from '@tadpoles/api-client'
import type { Guardian } from '@tadpoles/common'

import type { SessionContext } from '../context/session-context.js'
import { createUserContext, type UserContext } from '../context/user-context.js'
import { deduplicateBy } from '../utils/deduplicate-by.js'
import type { FeatureAssignment } from './persistence/stores/feature-store.js'
import type { UserScopedDomainData } from './user-scoped-services.svelte.js'

type ParametersPayload = Awaited<ReturnType<ApiClient['parameters']['get']>>

export type ParametersSnapshot = {
    context: UserContext
    data: UserScopedDomainData
}

export class ParametersService {
    readonly #apiClient: ApiClient

    public constructor(apiClient: ApiClient) {
        this.#apiClient = apiClient
    }

    public async fetch(
        sessionContext: SessionContext,
        signal: AbortSignal
    ): Promise<ParametersSnapshot> {
        void sessionContext.namespace
        const payload = await this.#apiClient.parameters.get({ signal })
        return this.#normalizePayload(payload)
    }

    #normalizePayload(payload: ParametersPayload): ParametersSnapshot {
        const guardians: Guardian[] = []

        for (const relationship of payload.relationships) {
            const childGuardians = (relationship.child.guardians ?? []).filter(
                (guardian) => guardian.key !== relationship.guardian.key
            )
            const relationshipGuardians = [relationship.guardian, ...childGuardians]
            guardians.push(...relationshipGuardians)
        }

        const features: FeatureAssignment[] = payload.featureSets.map(({ key, features }) => ({
            childKey: key,
            features
        }))

        return {
            context: createUserContext(payload.person),
            data: {
                classrooms: payload.classrooms,
                children: payload.relationships.map((relationship) => {
                    const childGuardians = relationship.child.guardians ?? []

                    return {
                        ...relationship.child,
                        guardians: deduplicateBy(
                            [relationship.guardian, ...childGuardians],
                            (guardian) => guardian.key
                        )
                    }
                }),
                features,
                guardians: deduplicateBy(guardians, (guardian) => guardian.key)
            }
        }
    }
}
